"use client"

import { useAuth } from "@/contexts/auth-context"
import { env } from "@/lib/env"
import { getBasePathUrl } from "@/lib/base-path"
import { tokenManager } from "@/lib/token-manager"

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public errorData?: any
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Extracts the server's error detail from an ApiError (Web API BadRequest body) */
export function getApiErrorDetail(err: unknown): string | null {
  if (err instanceof ApiError) {
    return err.errorData?.Message ?? err.errorData?.message ?? err.errorData?.detail ?? null
  }
  return null
}

const pascalToCamel = (str: string): string => str.charAt(0).toLowerCase() + str.slice(1)

/**
 * Recursively transforms object keys from PascalCase (Web API responses) to
 * camelCase. Writes don't need the inverse — model binding is case-insensitive.
 */
export function transformPascalToCamel(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(transformPascalToCamel)
  }
  if (obj !== null && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [pascalToCamel(key), transformPascalToCamel(value)])
    )
  }
  return obj
}

// API endpoints
export const API_ENDPOINTS = {
  // Main API
  MAIN: {
    BASE_URL: env.apiUrl,
    // Add specific endpoints here
    ATS_FIELD_MAPPING_NAMES: "/api/AtsFieldMapping/mapping-names",
    ATS_FIELD_MAPPING_DESTINATION_FIELDS: (mappingName: string) => `/api/AtsFieldMapping/destination-fields/${mappingName}`,
    ATS_FIELD_MAPPINGS: (mappingName: string) => `/api/AtsFieldMapping/mappings/${mappingName}`,
    ATS_VALUE_MAPPING_SETS: "/api/AtsFieldMapping/value-mapping-sets",
    ATS_VALUE_MAPPING_SET: (name: string) => `/api/AtsFieldMapping/value-mapping-sets/${encodeURIComponent(name)}`,
    // ATS source management
    ATS_SOURCES: "/api/AtsSource/sources",
    ATS_SOURCE_ACTIVATE: (code: string) => `/api/AtsSource/sources/${encodeURIComponent(code)}/activate`,
    ATS_SYSTEMS: "/api/AtsSource/ats-systems",
    ATS_ACTIVE_SOURCE: "/api/AtsSource/active",
    // Event sync processor configuration (AtsVendorConfiguration documents)
    ATS_VENDOR_CONFIGURATIONS: "/api/AtsVendorConfiguration/configurations",
    ATS_VENDOR_CONFIGURATION: (id: string) => `/api/AtsVendorConfiguration/configurations/${encodeURIComponent(id)}`,
    // FBO -> target translation layer
    TARGET_NAMES: "/api/FboTargetMapping/targets",
    TARGET_SPECS: (targetName: string) => `/api/FboTargetMapping/target-specs/${targetName}`,
    TARGET_SOURCE_FIELDS: (mappingName: string) => `/api/FboTargetMapping/target-source-fields/${mappingName}`,
    TARGET_MAPPINGS: (targetName: string) => `/api/FboTargetMapping/target-mappings/${targetName}`,
  },
  // Admin Service API
  ADMIN: {
    BASE_URL: env.adminServiceApiEndpoint,
    // Tenant theme customization (same document appNext's ThemeService reads)
    TENANT_THEME: "/GlobalConfiguration/Configuration/TenantTheme",
  },
  // ATS Integration API
  ATS_INTEGRATION: {
    BASE_URL: env.atsIntegrationApiUrl,
    // Add specific endpoints here
    MAP: (atsSystem: string) => `/AtsIntegration/${atsSystem}/composite-mapped-data`,
    INFO: (atsSystem: string) => `/AtsIntegration/${atsSystem}/info`,
    RECONNECT: (atsSystem: string) => `/AtsIntegration/${atsSystem}/reconnect`,
    // ATS Explorer
    EXPLORER_ENTITY_TYPES: (atsSystem: string) => `/AtsExplorer/${atsSystem}/entity-types`,
    EXPLORER_ENTITY_TYPE: (atsSystem: string, entityType: string) =>
      `/AtsExplorer/${atsSystem}/entity-types/${encodeURIComponent(entityType)}`,
    EXPLORER_RECORDS: (atsSystem: string) => `/AtsExplorer/${atsSystem}/records`,
    EXPLORER_RAW_RECORD: (atsSystem: string, entityType: string, id: string) =>
      `/AtsExplorer/${atsSystem}/records/${entityType}/${id}`,
    EXPLORER_SCHEMA: (atsSystem: string, entityType: string) => `/AtsExplorer/${atsSystem}/schema/${entityType}`,
    EXPLORER_REFERENCE_DATA_TYPES: (atsSystem: string) => `/AtsExplorer/${atsSystem}/reference-data-types`,
    EXPLORER_REFERENCE_DATA: (atsSystem: string, referenceType: string) =>
      `/AtsExplorer/${atsSystem}/reference-data/${referenceType}`,
    EXPLORER_PULL: (atsSystem: string) => `/AtsExplorer/${atsSystem}/pull`,
    EXPLORER_MAPPED_PULL: (atsSystem: string) => `/AtsExplorer/${atsSystem}/mapped-pull`,
  },
}

// Custom hook for making authenticated API requests
export function useApi() {
  const { token, requestTokenFromParent } = useAuth()

  // Generic fetch function with authentication and token retry logic
  const fetchWithAuth = async <T,>(url: string, options: RequestInit = {}): Promise<T> => {
    const headers = new Headers(options.headers)

    // If no token is available, request one and queue this request
    if (!token) {
      // Request token from parent if we haven't already
      if (tokenManager.shouldRequestToken()) {
        requestTokenFromParent()
      }

      // Apply base path if the URL is relative and doesn't start with http or //
      let finalUrl = url
      if (!url.startsWith("http") && !url.startsWith("//")) {
        finalUrl = getBasePathUrl(url)
      }
      const retriedResponse = await tokenManager.queueRequest(finalUrl, {
        ...options,
        headers,
      })

          // Process the retried response
          if (!retriedResponse.ok) {
            const errorData = await retriedResponse.json().catch(() => ({}))
            throw new ApiError(
              `API request failed with status ${retriedResponse.status}`,
              retriedResponse.status,
              errorData
            )
          }

      // For non-JSON responses, return the response object
      const contentType = retriedResponse.headers.get("content-type")
      if (contentType && contentType.includes("application/json")) {
        return retriedResponse.json()
      }

      return retriedResponse as unknown as T
    }

    // Add authorization header if token exists
    headers.set("Authorization", `Bearer ${token}`)

    // Apply base path if the URL is relative and doesn't start with http or //
    let finalUrl = url
    if (!url.startsWith("http") && !url.startsWith("//")) {
      finalUrl = getBasePathUrl(url)
    }

    try {
      const response = await fetch(finalUrl, {
        ...options,
        headers,
      })

      if (!response.ok) {
        // Handle 401 Unauthorized errors
        if (response.status === 401) {
          // Request a new token from parent
          if (tokenManager.shouldRequestToken()) {
            requestTokenFromParent()
          }
          const retriedResponse = await tokenManager.queueRequest(finalUrl, {
            ...options,
            headers,
          })

          // Process the retried response
          if (!retriedResponse.ok) {
            const errorData = await retriedResponse.json().catch(() => ({}))
            throw new ApiError(
              `API request failed with status ${retriedResponse.status}`,
              retriedResponse.status,
              errorData
            )
          }

          // For non-JSON responses, return the response object
          const contentType = retriedResponse.headers.get("content-type")
          if (contentType && contentType.includes("application/json")) {
            return retriedResponse.json()
          }

          return retriedResponse as unknown as T
        }

        // Handle other error responses
        const errorData = await response.json().catch(() => ({}))
        throw new ApiError(
          `API request failed with status ${response.status}`,
          response.status,
          errorData
        )
      }

      // For non-JSON responses, return the response object
      const contentType = response.headers.get("content-type")
      if (contentType && contentType.includes("application/json")) {
        const jsonResponse = await response.json()
        return jsonResponse
      }

      return response as unknown as T
    } catch (error) {
      // Rethrow the error
      throw error
    }
  }

  // Helper methods for common HTTP methods
  const get = <T,>(endpoint: string, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, { ...options, method: "GET" })

  const post = <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, {
      ...options,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      body: JSON.stringify(data),
    })

  const put = <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, {
      ...options,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      body: JSON.stringify(data),
    })

  const patch = <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, {
      ...options,
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      body: JSON.stringify(data),
    })

  const del = <T,>(endpoint: string, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, { ...options, method: "DELETE" })

  // Create full URLs for each API
  const createUrl = (baseUrl: string, path: string) => {
    // Remove trailing slash from base URL if present
    const base = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl
    // Remove leading slash from path if present
    const cleanPath = path.startsWith("/") ? path.slice(1) : path
    return `${base}/${cleanPath}`
  }

  // API-specific methods
  const mainApi = {
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), data, options),
    patch: <T,>(path: string, data: any, options?: RequestInit) =>
      patch<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), options),
  }

  const adminApi = {
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    patch: <T,>(path: string, data: any, options?: RequestInit) =>
      patch<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
  }

  const atsIntegrationApi = {
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.ATS_INTEGRATION.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.ATS_INTEGRATION.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.ATS_INTEGRATION.BASE_URL, path), data, options),
    patch: <T,>(path: string, data: any, options?: RequestInit) =>
      patch<T>(createUrl(API_ENDPOINTS.ATS_INTEGRATION.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.ATS_INTEGRATION.BASE_URL, path), options),
  }

  return {
    mainApi,
    adminApi,
    atsIntegrationApi,
    fetchWithAuth,
  }
}
